/*  download_manual_trades.tsx
    – Ops view of manual trades (sukuk / money market) for the settings page.
      • "Trades Awaiting Geneva": every manual trade across ALL trade dates whose
        state is not LOCKED (collection-group query), with inline Send, so a trade
        added on an old date is never missed and never requires switching dates.
      • "Manual Trades": every trade dated the selected date, with inline Send.
      • "Sent on this date": trades SENT on the selected date but dated elsewhere
        (via sentAt). One document, two views: a same-day send shows once, an
        old-dated send shows under both its trade date and its sending date.
      The cross-date panels are collapsed by default.
*/
import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import {
    Table,
    Card,
    Text,
    Group,
    ActionIcon,
    NumberFormatter,
    Loader,
    Center,
    Modal,
    Badge,
    Tooltip,
    Button,
    Stack,
    ThemeIcon,
    HoverCard,
    Accordion,
} from '@mantine/core';
import { IconEye, IconCheck, IconX, IconAlertTriangle, IconSend } from '@tabler/icons-react';
import {
    collection, collectionGroup, getDocs, getDoc, onSnapshot, query, where, getFirestore,
    doc, updateDoc, deleteField, arrayUnion, Timestamp, DocumentReference,
} from 'firebase/firestore';
import { BaseResult, CustomClaims, GenerateXmlRequest, ManualTrade, Portfolio } from '../../types';
import { format, parse, startOfDay, addDays, isSameDay } from 'date-fns';
import { httpsCallable } from 'firebase/functions';
import { useFunctions } from '../../helpers/firebaseContext';
import { notifications } from '@mantine/notifications';
import { convertManualTradeToGeneva, convertNewIssuerToGeneva } from '../../helpers/genevaHelper';
import { MyUserDataContext } from '../../contexts';
import ManualTradeView from './manualTradeVIew';


// --- Helper Functions ---
// Format date to DD-MM-YYYY for Firebase path
const formatDateForPath = (date: Date): string => format(date, 'dd-MM-yyyy');
const parseDateId = (dateId: string): Date => parse(dateId, 'dd-MM-yyyy', new Date());

// Geneva refuses a new Bond whose ISIN already exists and names the existing code:
// "Object with same secondary key is found in AGA - Bond[9100011]::Isin=XS3474553776"
const DUPLICATE_BOND_RE = /Bond\[(\d+)\]::Isin=/i;
// Geneva refuses an Issuer it already has (e.g. created by another trade, or by hand)
// — the issuer exists, which is all the bond needs.
const ISSUER_EXISTS_RE = /already exist|duplicate|must be unique|same secondary key|no change|nothing changed/i;

type TradeRow = { dateId: string; trade: ManualTrade };

// Turns a raw geneva_responses key (e.g. "new_sukuk") into a label ("New Sukuk").
const formatGenevaKey = (key: string): string =>
    key.split('_').filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// Extracts the error rows (key + message) from a trade's geneva_responses. Prefers
// the failing entries; falls back to all entries when none are explicitly flagged.
const getGenevaErrorRows = (trade: ManualTrade): { key: string; message: string }[] => {
    const responses = trade.geneva_responses;
    if (!responses) return [];
    const all = Object.entries(responses);
    const failing = all.filter(([, v]) => v?.status && v.status !== 'Success');
    const rows = failing.length > 0 ? failing : all;
    return rows.map(([key, v]) => ({ key, message: v?.status_message || v?.status || '-' }));
};

// Cash currency a BUY settles in: the sukuk's principal currency (default USD),
// or the money market instrument's currency (USD, else KWD).
const settlementCurrency = (trade: ManualTrade): string =>
    trade.type === 'sukuk'
        ? (trade.sukuk?._PRINCIPAL_CURRENCY_CODE_ || 'USD')
        : (trade.moneyMarket?.currency === 'USD' ? 'USD' : 'KWD');

// Available cash in the trade's portfolio for its settlement currency.
// KWD is the local currency; every other currency is held as foreign cash.
const getAvailableCash = (trade: ManualTrade, pmap: Record<string, Portfolio>): number => {
    const portfolio = pmap[trade.portfolioId];
    if (!portfolio) return 0;
    const currency = settlementCurrency(trade);
    if (currency === 'KWD') {
        return portfolio.position?.local_position?.find((p) => p.code === 'KWD')?.total_market_value ?? 0;
    }
    return portfolio.position?.foreign_position?.find((p) => p.code === currency && p.cash === true)?.total_market_value ?? 0;
};

// --- Main Component ---
const DownloadManualTradesTable: React.FC<{ date: Date | null; onDateChange?: (date: Date) => void }> = ({ date, onDateChange }) => {
    const db = getFirestore();
    const functions = useFunctions();
    const userContext = useContext(MyUserDataContext);
    const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};
    const canSeeAllTrades = !!userClaims.admin || !!userClaims.oper;
    const [loading, setLoading] = useState(true);
    // Manual trades dated the selected date.
    const [dateTrades, setDateTrades] = useState<ManualTrade[]>([]);
    // Manual trades SENT on the selected date (any trade date), via sentAt.
    const [sentOnDateRows, setSentOnDateRows] = useState<TradeRow[]>([]);
    const [modalOpened, setModalOpened] = useState(false);
    const [selectedTrade, setSelectedTrade] = useState<ManualTrade | null>(null);
    // The trade-date (dd-MM-yyyy) the currently viewed trade belongs to, so cash
    // validation and doc updates target the right date.
    const [selectedTradeDateId, setSelectedTradeDateId] = useState<string | null>(null);
    // All manual trades (across every trade date) that are not yet LOCKED.
    const [unlockedTrades, setUnlockedTrades] = useState<TradeRow[]>([]);
    const [unlockedLoading, setUnlockedLoading] = useState(false);
    // Per-trade loading flags for inline "Send" actions, keyed by trade id.
    const [sendingIds, setSendingIds] = useState<Record<string, boolean>>({});
    // Trades sent this session. Once a trade locks it drops out of the live
    // "state != LOCKED" query, so we keep it here (forced to LOCKED) so it stays
    // visible in the awaiting panel as confirmation. Session-only.
    const [lockedOverrides, setLockedOverrides] = useState<Record<string, TradeRow>>({});
    // Cache of portfolios keyed by trade-date id, so we can validate cash for any
    // date without refetching repeatedly.
    const portfoliosCacheRef = useRef<Record<string, Record<string, Portfolio>>>({});
    const generateXml = httpsCallable<GenerateXmlRequest, BaseResult>(functions, 'generateGenevaXml');
    const orgId = 'demo';

    const dateId = date ? formatDateForPath(date) : null;

    // Returns the portfolios map for a trade date, fetching + caching on first use.
    // Falls back to the top-level portfolios collection when the date has none.
    const ensurePortfoliosForDate = useCallback(async (id: string): Promise<Record<string, Portfolio>> => {
        if (portfoliosCacheRef.current[id]) return portfoliosCacheRef.current[id];
        let snapshot = await getDocs(collection(db, `trade_dates/${id}/portfolios`));
        if (snapshot.empty) snapshot = await getDocs(collection(db, 'portfolios'));
        const map: Record<string, Portfolio> = {};
        snapshot.forEach((d) => { map[d.id] = { id: d.id, ...d.data() } as Portfolio; });
        portfoliosCacheRef.current[id] = map;
        return map;
    }, [db]);

    // Real-time list of manual trades dated the selected date.
    useEffect(() => {
        if (!dateId) { setLoading(false); setDateTrades([]); return; }
        setLoading(true);
        const q = query(collection(db, `trade_dates/${dateId}/manual_trades`));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const trades = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as ManualTrade));
            trades.sort((a, b) => a.portfolioId.localeCompare(b.portfolioId));
            setDateTrades(trades);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching manual trades:', error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [dateId, db]);

    // Real-time list of manual trades SENT on the selected date (sentAt within the
    // day), whatever their trade date. Admin/oper only (collection-group rule).
    useEffect(() => {
        if (!date || !canSeeAllTrades) { setSentOnDateRows([]); return; }
        const start = startOfDay(date);
        const q = query(
            collectionGroup(db, 'manual_trades'),
            where('sentAt', '>=', Timestamp.fromDate(start)),
            where('sentAt', '<', Timestamp.fromDate(addDays(start, 1))),
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const rows: TradeRow[] = [];
            snapshot.forEach((docSnap) => {
                const tradeDateId = docSnap.ref.parent.parent?.id;
                if (tradeDateId) rows.push({ dateId: tradeDateId, trade: { id: docSnap.id, ...docSnap.data() } as ManualTrade });
            });
            setSentOnDateRows(rows);
        }, (error) => console.error('Error listening to trades sent on date:', error));
        return () => unsubscribe();
    }, [date, canSeeAllTrades, db]);

    // Warm the portfolios cache for the selected date so the first cash check is fast.
    useEffect(() => {
        if (dateId) ensurePortfoliosForDate(dateId).catch((e) => console.error('Error fetching portfolios:', e));
    }, [dateId, ensurePortfoliosForDate]);

    // Cross-date listener: any manual trade (across all trade dates) that is not
    // LOCKED yet. Every trade carries a state (PENDING on create, kept by the
    // trigger), so "!= LOCKED" is exhaustive. Admin/oper only.
    useEffect(() => {
        if (!canSeeAllTrades) { setUnlockedTrades([]); setUnlockedLoading(false); return; }
        setUnlockedLoading(true);
        const q = query(collectionGroup(db, 'manual_trades'), where('state', '!=', 'LOCKED'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const rows: TradeRow[] = [];
            snapshot.forEach((docSnap) => {
                const tradeDateId = docSnap.ref.parent.parent?.id;
                if (tradeDateId) rows.push({ dateId: tradeDateId, trade: { id: docSnap.id, ...docSnap.data() } as ManualTrade });
            });
            setUnlockedTrades(rows);
            setUnlockedLoading(false);
        }, (error) => {
            console.error('Error listening to unlocked manual trades:', error);
            setUnlockedLoading(false);
        });
        return () => unsubscribe();
    }, [canSeeAllTrades, db]);

    // Awaiting panel rows: live non-locked trades merged with this-session sends
    // (override wins so a just-sent trade shows as LOCKED). Newest date first.
    const pendingRows = useMemo(() => {
        const byId: Record<string, TradeRow> = {};
        for (const row of unlockedTrades) if (row.trade.id) byId[row.trade.id] = row;
        for (const [id, row] of Object.entries(lockedOverrides)) byId[id] = row;
        return Object.values(byId)
            .map((r) => ({ ...r, date: parseDateId(r.dateId) }))
            .sort((a, b) => (b.date.getTime() - a.date.getTime()) || a.trade.portfolioId.localeCompare(b.trade.portfolioId));
    }, [unlockedTrades, lockedOverrides]);

    const pendingDateCount = useMemo(() => new Set(pendingRows.map((r) => r.dateId)).size, [pendingRows]);
    const awaitingCount = useMemo(() => pendingRows.filter((r) => r.trade.state !== 'LOCKED').length, [pendingRows]);

    const dateLockedCount = useMemo(() => dateTrades.filter((t) => t.state === 'LOCKED').length, [dateTrades]);

    // Trades sent on the selected date but dated elsewhere. Same-date sends are
    // already in dateTrades, so they are dropped here and appear exactly once.
    const sentOnDateElsewhere = useMemo(
        () => sentOnDateRows
            .filter((r) => r.dateId !== dateId)
            .map((r) => ({ ...r, date: parseDateId(r.dateId) }))
            .sort((a, b) => (b.date.getTime() - a.date.getTime()) || a.trade.portfolioId.localeCompare(b.trade.portfolioId)),
        [sentOnDateRows, dateId],
    );

    const handleViewClick = (trade: ManualTrade, tradeDateId: string | null = dateId) => {
        setSelectedTrade(trade);
        setSelectedTradeDateId(tradeDateId);
        setModalOpened(true);
    };

    // Stale geneva_responses block a re-send (the state is derived from them and the
    // loader write-back merges into them), so before sending again we move them to
    // geneva_responses_history for audit and clear the live field. Also stamps who
    // pressed Send; sentAt itself is written by the trigger when the trade locks.
    const archiveResponsesAndStampSender = async (tradeRef: DocumentReference, trade: ManualTrade | undefined) => {
        const responses = trade?.geneva_responses;
        const hasResponses = !!responses && Object.keys(responses).length > 0;
        await updateDoc(tradeRef, {
            sentBy: userContext?.user?.displayName || '',
            sentById: userContext?.user?.uid || '',
            ...(hasResponses ? {
                geneva_responses: deleteField(),
                geneva_responses_history: arrayUnion({ responses, archivedAt: Timestamp.now() }),
            } : {}),
        });
    };

    // Sends (or removes) a trade to/from Geneva. Throws on failure so callers can
    // surface the message. BUY trades are guarded against insufficient portfolio
    // cash using the positions for the trade's own date.
    async function handleSendToGeneva(trade: ManualTrade, tradeDateId: string | null, isUndo: boolean = false): Promise<ManualTrade> {
        if (!trade) throw new Error('No trade selected');
        const rowDateId = tradeDateId ?? dateId;
        if (!rowDateId || !trade.id) throw new Error('Trade has no id or trade date.');
        const tradeRef = doc(db, `trade_dates/${rowDateId}/manual_trades`, trade.id);

        // The row's trade can predate trigger writes (e.g. the new sukuk's CODE), so send
        // the doc's current state, and build the request before anything reaches Geneva.
        const current = (await getDoc(tradeRef)).data() as ManualTrade | undefined;
        if (current) trade = { ...trade, ...current, id: trade.id };
        const request = convertManualTradeToGeneva(trade);

        // Undo (removal from Geneva) and SELL trades are not cash-constrained.
        if (!isUndo && trade.transactionType === 'BUY') {
            const pmap = await ensurePortfoliosForDate(rowDateId);
            const required = trade.type === 'sukuk' ? trade.settlementAmount : trade.nominalValue;
            const available = getAvailableCash(trade, pmap);
            const currency = settlementCurrency(trade);
            if (available < required) {
                throw new Error(
                    `Insufficient cash in portfolio ${trade.portfolioId}: available ${available.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}, required ${required.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}.`
                );
            }
        }

        // Re-send of a non-locked trade: archive previous responses first.
        if (!isUndo && current?.state !== 'LOCKED') await archiveResponsesAndStampSender(tradeRef, current);

        // New Issuer (LSEG issuer with no Geneva match) must exist before the bond
        // references it. Sent once per trade+name (newIssuerCreated), so re-sends skip
        // it; "already exists" from Geneva counts as done (another trade or ops made it).
        // Undo leaves it in Geneva (other bonds may use it).
        const issuerName = trade.sukuk?._ISSUER_NAMESORT_?.trim();
        if (!isUndo && trade.type === 'sukuk' && trade.transactionType === 'BUY' && !trade.selectedSukukId && trade.sukuk?.NEW_ISSUER && trade.newIssuerCreated !== issuerName) {
            // Clear the previous status so a stale Success can't hide a missing write.
            await updateDoc(tradeRef, { geneva_response: deleteField() });
            await generateXml({ request: convertNewIssuerToGeneva(trade), orgId, isApproval: true });
            const issuerResponse = ((await getDoc(tradeRef)).data() as ManualTrade | undefined)?.geneva_response;
            if (issuerResponse?.status !== 'Success' && !ISSUER_EXISTS_RE.test(issuerResponse?.status_message ?? '')) {
                throw new Error(`Issuer "${issuerName}" creation failed: ${issuerResponse?.status_message || 'no response from Geneva'}`);
            }
            await updateDoc(tradeRef, { newIssuerCreated: issuerName });
        }

        await generateXml({ request, orgId, isApproval: true, ...(isUndo && { isUndo: true }) });

        // Duplicate-bond recovery (one shot): creating a new sukuk fails when its
        // ISIN already exists in Geneva. Link the trade to the existing code and
        // re-send once as a buy of the existing sukuk. The retry calls generateXml
        // directly (not this handler), so it can never loop; and once
        // selectedSukukId is set this branch is skipped on later sends.
        if (!isUndo && trade.type === 'sukuk' && trade.transactionType === 'BUY' && !trade.selectedSukukId) {
            const fresh = (await getDoc(tradeRef)).data() as ManualTrade | undefined;
            const existingCode = DUPLICATE_BOND_RE.exec(fresh?.geneva_responses?.new_sukuk?.status_message ?? '')?.[1];
            if (existingCode && !fresh?.duplicateSukukRecovery) {
                // Keep the failed create/buy responses and the attempted code for audit.
                await archiveResponsesAndStampSender(tradeRef, fresh);
                await updateDoc(tradeRef, {
                    selectedSukukId: existingCode,
                    'sukuk.CODE': existingCode,
                    duplicateSukukRecovery: {
                        existingCode,
                        attemptedCode: fresh?.sukuk?.CODE ?? null,
                        recoveredAt: Timestamp.now(),
                    },
                });
                const linkedTrade: ManualTrade = { ...trade, selectedSukukId: existingCode, sukuk: { ...trade.sukuk!, CODE: existingCode } };
                setSelectedTrade((prev) => (prev?.id === trade.id ? linkedTrade : prev));
                await generateXml({ request: convertManualTradeToGeneva(linkedTrade), orgId, isApproval: true });
                notifications.show({
                    title: 'Linked to existing Sukuk',
                    message: `ISIN ${trade.sukuk?.ISIN} already exists in Geneva as ${existingCode}. Trade re-sent once as a buy of the existing Sukuk.`,
                    color: 'blue',
                });
                return linkedTrade;
            }
        }
        return trade;
    }

    const showSendError = (err: unknown) => {
        console.error('Error sending to Geneva:', err);
        notifications.show({
            color: 'red',
            title: 'Geneva error',
            message: err instanceof Error && err.message ? err.message : 'Failed to send. Check console for details.',
            withCloseButton: true,
            icon: <IconX size={16} />,
        });
    };

    // Inline send from the awaiting panel, with per-trade loading + notifications.
    const handleQuickSend = async (trade: ManualTrade, tradeDateId: string) => {
        if (!trade.id) return;
        const id = trade.id;
        setSendingIds((prev) => ({ ...prev, [id]: true }));
        try {
            const sent = await handleSendToGeneva(trade, tradeDateId, false);
            // Keep the row visible, now marked LOCKED, instead of letting it vanish.
            setLockedOverrides((prev) => ({ ...prev, [id]: { dateId: tradeDateId, trade: { ...sent, state: 'LOCKED' } } }));
            notifications.show({
                color: 'green',
                title: 'Sent to Geneva',
                message: `Trade sent successfully (${tradeDateId}).`,
                withCloseButton: true,
                icon: <IconCheck size={16} />,
            });
        } catch (err) {
            showSendError(err);
        } finally {
            setSendingIds((prev) => { const next = { ...prev }; delete next[id]; return next; });
        }
    };

    // State badge. For GENEVA_ERROR, hovering reveals a small table of the failing
    // Geneva responses (key + status message), since the response keys vary.
    const renderStateBadge = (trade: ManualTrade) => {
        const state = trade.state ?? 'PENDING';
        const color = state === 'LOCKED' ? 'teal' : state === 'GENEVA_ERROR' ? 'red' : 'gray';
        const badge = <Badge size="sm" variant="light" color={color} style={state === 'GENEVA_ERROR' ? { cursor: 'help' } : undefined}>{state}</Badge>;
        if (state !== 'GENEVA_ERROR') return badge;
        const errorRows = getGenevaErrorRows(trade);
        if (errorRows.length === 0) return badge;
        return (
            <HoverCard width={420} shadow="md" withArrow position="top-start" openDelay={120}>
                <HoverCard.Target>{badge}</HoverCard.Target>
                <HoverCard.Dropdown>
                    <Text fw={600} size="sm" mb={6} c="red">Geneva error{errorRows.length > 1 ? 's' : ''}</Text>
                    <Table fz="xs" verticalSpacing={4} withRowBorders={false}>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Key</Table.Th>
                                <Table.Th>Status message</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {errorRows.map(({ key, message }) => (
                                <Table.Tr key={key}>
                                    <Table.Td><Text size="xs" fw={500}>{formatGenevaKey(key)}</Text></Table.Td>
                                    <Table.Td><Text size="xs">{message}</Text></Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </HoverCard.Dropdown>
            </HoverCard>
        );
    };

    // "sent dd MMM" chip when the trade was sent on a different day than its trade date.
    const renderSentChip = (trade: ManualTrade, rowDateId: string) => {
        const sentAt = trade.sentAt?.toDate?.();
        if (!sentAt || isSameDay(sentAt, parseDateId(rowDateId))) return null;
        return (
            <Tooltip label={`Sent ${format(sentAt, 'dd MMM yyyy HH:mm')}${trade.sentBy ? ` by ${trade.sentBy}` : ''}`} withArrow>
                <Badge size="xs" variant="outline" color="teal">sent {format(sentAt, 'dd MMM')}</Badge>
            </Tooltip>
        );
    };

    // Single trade row, shared by every table. `rowDate` adds a Date column whose
    // value jumps the page to that trade date.
    const renderTradeRow = (trade: ManualTrade, rowDateId: string, rowDate: Date | null) => {
        const desc = trade.sukuk?.DESCRIPTION || trade.moneyMarket?.description || 'N/A';
        const isSending = !!(trade.id && sendingIds[trade.id]);
        const isLocked = trade.state === 'LOCKED';
        return (
            <Table.Tr key={`${rowDateId}-${trade.id}`}>
                {rowDate && (
                    <Table.Td>
                        <Group gap={4} wrap="nowrap">
                            <Tooltip label="Go to this trade date" withArrow disabled={!onDateChange}>
                                <Text size="sm" fw={500} style={onDateChange ? { cursor: 'pointer' } : undefined} onClick={() => onDateChange?.(rowDate)}>
                                    {format(rowDate, 'dd MMM yyyy')}
                                </Text>
                            </Tooltip>
                            {rowDateId === dateId && <Badge size="xs" variant="light" color="blue">current</Badge>}
                        </Group>
                    </Table.Td>
                )}
                <Table.Td>
                    <Group gap={6} wrap="nowrap">
                        <Badge size="xs" color={trade.type === 'sukuk' ? 'blue' : 'grape'} variant="light">{trade.type === 'sukuk' ? 'SUKUK' : 'MM'}</Badge>
                        <Text size="sm" lineClamp={1} style={{ maxWidth: 240 }}>{desc}</Text>
                    </Group>
                </Table.Td>
                <Table.Td><Text size="sm">{trade.portfolioId}</Text></Table.Td>
                <Table.Td><Badge color={trade.transactionType === 'BUY' ? 'green' : 'red'} variant="light" size="sm">{trade.transactionType}</Badge></Table.Td>
                <Table.Td ta="right"><NumberFormatter value={trade.settlementAmount} thousandSeparator decimalScale={2} /></Table.Td>
                <Table.Td><Group gap={6} wrap="nowrap">{renderStateBadge(trade)}{renderSentChip(trade, rowDateId)}</Group></Table.Td>
                <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                        <Tooltip label="View details" withArrow>
                            <ActionIcon variant="light" color="blue" onClick={() => handleViewClick(trade, rowDateId)}><IconEye size={16} /></ActionIcon>
                        </Tooltip>
                        <Button size="xs" variant="light" leftSection={<IconSend size={14} />} loading={isSending} disabled={isLocked} onClick={() => handleQuickSend(trade, rowDateId)}>
                            {isLocked ? 'Sent' : 'Send'}
                        </Button>
                    </Group>
                </Table.Td>
            </Table.Tr>
        );
    };

    const tableHead = (withDate: boolean) => (
        <Table.Thead>
            <Table.Tr>
                {withDate && <Table.Th>Date</Table.Th>}
                <Table.Th>Instrument</Table.Th>
                <Table.Th>Portfolio</Table.Th>
                <Table.Th>Side</Table.Th>
                <Table.Th ta="right">Settlement</Table.Th>
                <Table.Th>State</Table.Th>
                <Table.Th ta="right">Actions</Table.Th>
            </Table.Tr>
        </Table.Thead>
    );

    // Every tracked trade that still needs operations action, across all dates,
    // plus this-session sends shown as LOCKED. Review or send straight from here.
    // Collapsed by default; the header badge carries the counts.
    const pendingPanel = (canSeeAllTrades && (unlockedLoading || pendingRows.length > 0)) ? (
        <Accordion variant="contained" radius="md" chevronPosition="left">
            <Accordion.Item value="awaiting">
                <Accordion.Control icon={<ThemeIcon color="yellow" variant="light" radius="xl"><IconAlertTriangle size={16} /></ThemeIcon>}>
                    <Group justify="space-between" pr="md" wrap="nowrap">
                        <Text fw={600}>Trades Awaiting Geneva</Text>
                        {unlockedLoading ? <Loader size="xs" /> : (
                            <Badge color={awaitingCount > 0 ? 'yellow' : 'green'} variant="light">
                                {awaitingCount} pending · {pendingDateCount} date{pendingDateCount !== 1 ? 's' : ''}
                            </Badge>
                        )}
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    <Table verticalSpacing={6} fz="sm" highlightOnHover>
                        {tableHead(true)}
                        <Table.Tbody>
                            {pendingRows.map(({ dateId: rowDateId, date: rowDate, trade }) => renderTradeRow(trade, rowDateId, rowDate))}
                        </Table.Tbody>
                    </Table>
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    ) : null;

    // Rendered in every branch so the View action works even when the selected
    // date has no trades of its own.
    const tradeModal = (
        <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title="Sukuk Deal Ticket" size="xl" centered>
            {selectedTrade && (
                <ManualTradeView
                    trade={selectedTrade}
                    onSendToGeneva={async (t, isUndo: boolean = false) => {
                        try {
                            const sent = await handleSendToGeneva(t, selectedTradeDateId, isUndo);
                            if (t.id) {
                                const rowDateId = selectedTradeDateId ?? dateId ?? '';
                                setLockedOverrides((prev) => {
                                    const next = { ...prev };
                                    if (isUndo) delete next[t.id!];
                                    else next[t.id!] = { dateId: rowDateId, trade: { ...sent, state: 'LOCKED' } };
                                    return next;
                                });
                            }
                            notifications.show({
                                color: 'green',
                                title: isUndo ? 'Removed from Geneva' : 'Sent to Geneva',
                                message: isUndo ? 'Trade removed successfully.' : 'Trade sent successfully.',
                                withCloseButton: true,
                                icon: <IconCheck size={16} />,
                            });
                        } catch (err) {
                            showSendError(err);
                        } finally {
                            setModalOpened(false);
                        }
                    }}
                />
            )}
        </Modal>
    );

    // Every trade dated the selected date, with inline Send.
    const dateCard = (
        <Card withBorder p="md" radius="md">
            <Group justify="space-between" mb="sm">
                <Text fw={500}>Manual Trades</Text>
                {!loading && (
                    <Group gap="xs">
                        <Badge variant="light" color="gray">{dateTrades.length} trade{dateTrades.length !== 1 ? 's' : ''}</Badge>
                        {dateTrades.length > 0 && (
                            <Badge variant="light" color={dateLockedCount === dateTrades.length ? 'teal' : 'yellow'}>{dateLockedCount} sent</Badge>
                        )}
                    </Group>
                )}
            </Group>
            {loading ? (
                <Center style={{ height: 100 }}><Loader /></Center>
            ) : dateTrades.length === 0 ? (
                <Text c="dimmed" ta="center" p="md">No manual trades dated {dateId}.</Text>
            ) : (
                <Table highlightOnHover verticalSpacing={6} fz="sm">
                    {tableHead(false)}
                    <Table.Tbody>
                        {dateTrades.map((trade) => renderTradeRow(trade, dateId!, null))}
                    </Table.Tbody>
                </Table>
            )}
        </Card>
    );

    // Trades sent on the selected date but dated elsewhere. Collapsed by default.
    const sentElsewherePanel = sentOnDateElsewhere.length > 0 ? (
        <Accordion variant="contained" radius="md" chevronPosition="left">
            <Accordion.Item value="sent-elsewhere">
                <Accordion.Control icon={<ThemeIcon color="teal" variant="light" radius="xl"><IconSend size={16} /></ThemeIcon>}>
                    <Group justify="space-between" pr="md" wrap="nowrap">
                        <Text fw={600}>Sent on {date ? format(date, 'dd MMM yyyy') : ''} (dated elsewhere)</Text>
                        <Badge variant="light" color="teal">{sentOnDateElsewhere.length} trade{sentOnDateElsewhere.length !== 1 ? 's' : ''}</Badge>
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    <Table highlightOnHover verticalSpacing={6} fz="sm">
                        {tableHead(true)}
                        <Table.Tbody>
                            {sentOnDateElsewhere.map(({ dateId: rowDateId, date: rowDate, trade }) => renderTradeRow(trade, rowDateId, rowDate))}
                        </Table.Tbody>
                    </Table>
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    ) : null;

    return (
        <Stack gap="md">
            {pendingPanel}
            {dateCard}
            {sentElsewherePanel}
            {tradeModal}
        </Stack>
    );
};

export default DownloadManualTradesTable;
